import { useEffect, useRef } from "react";

export default function StarfieldCanvas({
  className = "",
  density = 0.00012,
  interactive = true,
}) {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let stars = [];
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.floor(w * h * density);
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.2 + 0.3,
        base: Math.random() * 0.45 + 0.25,
        speed: Math.random() * 0.015 + 0.006,
        phase: Math.random() * Math.PI * 2,
        depth: Math.random() * 0.8 + 0.2,
        hue:
          Math.random() > 0.88
            ? "167,139,250"
            : Math.random() > 0.76
              ? "125,211,252"
              : "240,242,248",
      }));
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 22;
      mouse.current.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 14;
    };

    const draw = (t) => {
      mouse.current.x += (mouse.current.tx - mouse.current.x) * 0.04;
      mouse.current.y += (mouse.current.ty - mouse.current.y) * 0.04;
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        const tw = reduce
          ? s.base
          : s.base + Math.sin(t * 0.001 * s.speed * 60 + s.phase) * 0.28;
        ctx.beginPath();
        ctx.arc(s.x + mouse.current.x * s.depth, s.y + mouse.current.y * s.depth, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.hue},${Math.max(0, tw)})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw(0);
    window.addEventListener("resize", resize);
    if (interactive && !reduce) window.addEventListener("mousemove", onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, [density, interactive]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
